import _ from 'lodash';

import { getColorDifference, isGrayscale } from './colors';
import { TILESET_LAYER_PREFIX } from '../constants/tilesets';
import { TILESET_TYPE_ATTRIBUTE, TILESET_TYPE_CROP } from '../types/tileset';

/**
 * Some tricky logic here.
 *
 * There are two kinds of tilesets:
 * 1. Tilesets with a continuous key that is not grayscale that also have discrete keys that *are* grayscale.
 * 2. Tilesets with just a discrete key (that is not grayscale).
 *
 * - For type 1, if the hovered color is greyscale, we need to highlight it in the discrete section. If it's not greyscale, we'll highlight the hovered color on the gradient.
 * - For type 2, we always highlight the hovered color in the discrete section.
 *
 * And, when we're highlighting a color in the discrete section, we need to pick
 * the closest color. Ideally, the set of discrete colors the tileset uses would
 * be the same as the set of colors our discrete key uses, but that's not
 * actually the case.
 *
 * @returns {{
 *   continuousColorToHighlight?: string;
 *   discreteColorToHighlight?: string;
 * }}
 */
export const getHighlightColors = ({ tileset, hoveredColor }) => {
  // If there's no hovered color, there's nothing to highlight.
  if (!hoveredColor || hoveredColor.length === 0) return {};

  const discreteKeyColors = tileset.discreteKey.map(({ hexCode }) => hexCode);
  const closestDiscreteColor = _.minBy(discreteKeyColors, (color) =>
    getColorDifference(color, hoveredColor),
  );

  const hoveredColorIsGrayscale = isGrayscale(hoveredColor);
  const hasDiscreteExactMatch = discreteKeyColors.includes(hoveredColor);
  const tilesetHasContinuousKey = tileset.continuousKey;

  /* Type 1️ */
  if (tilesetHasContinuousKey) {
    // For grayscale color, return the closest discrete key color:
    if (hoveredColorIsGrayscale) {
      return { discreteColorToHighlight: closestDiscreteColor };
    }

    // For non-grayscale color, highlight it on the gradient:
    return { continuousColorToHighlight: hoveredColor };
  }

  /* Type 2 */
  // If the color is grayscale, we shouldn't highlight any color, since the discrete key is not grayscale.
  if (hoveredColorIsGrayscale) {
    // One exception is if the discrete key contains this exact color, which sometimes happens (non-grayscale keys sometimes contain black or white).
    if (hasDiscreteExactMatch) {
      return { discreteColorToHighlight: hoveredColor };
    }

    return {};
  }

  // Return the nearest color in the discrete key colors:
  return { discreteColorToHighlight: closestDiscreteColor };
};

export const layerIdForTileset = (tilesetId) =>
  `${TILESET_LAYER_PREFIX}|${tilesetId}`;

/**
 * Converts lat/lng info to a tile URL as well as providing offsets for where
 * the point is within the tile.
 *
 * The offsets are both in the range 0.0 to 1.0 and have the origin in the bottom left.
 *
 * NOTE: this is specifically for TMS tiles (the kind we use), not XYZ tiles.
 * The two are very similar but differ in the y axis (TMS y axis is flipped
 * relative to XYZ). For reference:
 * - https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames
 * - https://wiki.osgeo.org/wiki/Tile_Map_Service_Specification
 * - https://gist.github.com/tmcw/4954720
 */
export const latLngToTileInfo = ({ lat, lng, zoom, tilesetId }) => {
  const { floor, log, cos, tan, PI } = Math;

  const zoomExp = 2 ** zoom;
  const latRad = (lat * PI) / 180;

  const xFull = ((lng + 180) / 360) * zoomExp;
  const yFull = ((1 - log(tan(latRad) + 1 / cos(latRad)) / PI) / 2) * zoomExp;
  const zFull = zoom;

  // Edge case!
  // If yFull is already an integer, we subtract one here so we can use the next
  // tile w/ a yOffset of 0.0 instead of this tile with a yOffset of 1.0.
  // This ensures we get the behavior we'd expect for edge cases (like coords
  // 0,0 using tile 512/512 with 0,0 offset instead of tile 512/511 with 0,1
  // offset).
  const yFloor = yFull === floor(yFull) ? yFull - 1 : floor(yFull);

  const x = floor(xFull);
  const y = zoomExp - 1 - yFloor; // since we're using TMS tiles, y is inverted
  const z = floor(zFull);

  const url = `${window.location.origin}/tileset/${tilesetId}/${z}/${x}/${y}.png`;

  const xOffset = xFull - x;
  const yOffset = 1 - (yFull - yFloor); // (y is inverted so we need to subtract the offset from 1)

  return { url, xOffset, yOffset };
};

export const isClimateTileset = (tileset) =>
  tileset != null &&
  tileset.type === TILESET_TYPE_ATTRIBUTE &&
  (tileset.name.startsWith('hardiness') || tileset.name === 'precipitation');

export const isLandUseTileset = (tileset) =>
  tileset != null &&
  tileset.type === TILESET_TYPE_ATTRIBUTE &&
  tileset.name.startsWith('lulc');

export const isSuitabilityTileset = (tileset) =>
  tileset != null && tileset.type === TILESET_TYPE_CROP;
